import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Box, Button, Flex, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconArrowRight, IconCalendar, IconClock, IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PerformanceReviewRound } from '../../../../api/generated/model/performanceReviewRound';
import { PerformanceReviewRoundStage } from '../../../../api/generated/model/performanceReviewRoundStage';
import { Eyebrow } from '../../parts/primitives';

const STAGE_ORDER: PerformanceReviewRoundStage[] = [
  'draft',
  'collect-essay',
  'collect-feedback',
  'summary',
  'finished',
];

const stageIndex = (s?: PerformanceReviewRoundStage) => (s ? STAGE_ORDER.indexOf(s) : 0);

interface MiniStepperProps {
  stage?: PerformanceReviewRoundStage;
  status?: string;
}

const MiniStepper: React.FC<MiniStepperProps> = ({ stage, status }) => {
  const idx = stageIndex(stage);
  const archived = status === 'archived' || status === 'closed';
  return (
    <Flex align="center" gap={4}>
      {STAGE_ORDER.map((s, i) => {
        const isDone = i < idx || archived;
        const isActive = i === idx && !archived;
        const bg = isActive
          ? 'var(--mantine-primary-color-filled)'
          : isDone
            ? 'var(--mantine-primary-color-light-color)'
            : 'var(--mantine-color-default-border)';
        const dotW = isActive ? 14 : 8;
        return (
          <React.Fragment key={s}>
            <Box
              style={{
                width: dotW,
                height: 8,
                borderRadius: 999,
                background: bg,
                boxShadow: isActive ? '0 0 0 3px var(--mantine-primary-color-light)' : 'none',
                transition: 'all 200ms ease',
              }}
            />
            {i < STAGE_ORDER.length - 1 && (
              <Box
                style={{
                  width: 14,
                  height: 2,
                  background: isDone
                    ? 'var(--mantine-primary-color-light-color)'
                    : 'var(--mantine-color-default-border)',
                  borderRadius: 999,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Flex>
  );
};

const statusToneMap: Record<string, { color: string; variant: 'light' | 'default'; key: string }> =
  {
    active: { color: 'teal', variant: 'light', key: 'perfReview.adminHome.status.active' },
    draft: { color: 'orange', variant: 'light', key: 'perfReview.adminHome.status.draft' },
    closed: { color: 'gray', variant: 'light', key: 'perfReview.adminHome.status.closed' },
    archived: { color: 'gray', variant: 'default', key: 'perfReview.adminHome.status.archived' },
  };

interface RoundCardProps {
  round: PerformanceReviewRound;
  onOpen: (id: string) => void;
  locale: string;
  isMobile?: boolean;
}

const formatDate = (d?: string, locale = 'ru-RU') => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
};

const daysUntil = (d?: string) => {
  if (!d) return null;
  const ms = new Date(d).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

export const RoundCard: React.FC<RoundCardProps> = ({ round, onOpen, locale, isMobile }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const status = round.status || 'draft';
  const isArchived = status === 'archived' || status === 'closed';
  const st = statusToneMap[status] || statusToneMap.draft;
  const stage = round.stage as PerformanceReviewRoundStage | undefined;
  const daysLeft = !isArchived ? daysUntil(round.endDate) : null;
  const deadlineTone: 'orange' | 'teal' | null =
    daysLeft == null ? null : daysLeft <= 5 ? 'orange' : 'teal';

  return (
    <Paper withBorder radius="lg" p={0} style={{ overflow: 'hidden' }}>
      <Box p={isMobile ? 'md' : 'lg'}>
        <Flex gap="lg" align="flex-start" wrap={isMobile ? 'wrap' : 'nowrap'}>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Group gap={6} mb={8} wrap="wrap">
              <Badge color={st.color} variant={st.variant} size="sm" radius="xl">
                {t(st.key)}
              </Badge>
              {stage && stage !== 'draft' && (
                <Badge variant="default" size="sm" radius="xl">
                  {t(`perfReview.home.stageLabel.${stage}`, { defaultValue: stage })}
                </Badge>
              )}
            </Group>
            <Title order={3} size="h4" mb={4} style={{ letterSpacing: '-0.005em' }}>
              {round.title}
            </Title>
            {round.description && (
              <Text size="sm" c="dimmed" lh={1.5} mb={8}>
                {round.description}
              </Text>
            )}
            <Group gap={8} wrap="wrap" align="center">
              <Group gap={6} wrap="nowrap" c="dimmed">
                <IconCalendar size={12} />
                <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatDate(round.startDate, locale)} — {formatDate(round.endDate, locale)}
                </Text>
              </Group>
              {round.createdAt && (
                <>
                  <Box
                    style={{
                      width: 3,
                      height: 3,
                      borderRadius: 999,
                      background: 'var(--mantine-color-dimmed)',
                    }}
                  />
                  <Group gap={6} wrap="nowrap">
                    <IconUsers size={12} style={{ color: 'var(--mantine-color-dimmed)' }} />
                    <Text size="xs" c="dimmed">
                      {t('perfReview.adminHome.round.createdOn', {
                        date: formatDate(round.createdAt, locale),
                      })}
                    </Text>
                  </Group>
                </>
              )}
            </Group>
          </Box>

          <Stack gap={10} align={isMobile ? 'flex-start' : 'flex-end'}>
            {deadlineTone && daysLeft != null && daysLeft >= 0 && (
              <Badge
                color={deadlineTone}
                variant="light"
                size="md"
                radius="xl"
                leftSection={<IconClock size={12} />}
              >
                {t('perfReview.adminHome.round.daysLeft', { count: daysLeft })}
              </Badge>
            )}
            <Button
              variant={isArchived ? 'subtle' : 'default'}
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() =>
                round._id &&
                (isArchived
                  ? navigate(`/performance-review/${round._id}/results`)
                  : onOpen(round._id))
              }
            >
              {isArchived
                ? t('perfReview.adminHome.round.openResults')
                : t('perfReview.adminHome.round.open')}
            </Button>
          </Stack>
        </Flex>
      </Box>

      <Box
        px={isMobile ? 'md' : 'lg'}
        py="sm"
        style={{
          borderTop: '1px solid var(--mantine-color-default-border)',
          background: 'var(--mantine-color-default-hover)',
        }}
      >
        <Flex align="center" justify="space-between" gap="md" wrap="wrap">
          <Box>
            <Eyebrow>{t('perfReview.adminHome.round.stageLabel')}</Eyebrow>
            <Box mt={8}>
              <MiniStepper stage={stage} status={status} />
            </Box>
          </Box>
          <Text size="xs" c="dimmed">
            {t('perfReview.adminHome.round.stageNow', {
              name: stage ? t(`perfReview.home.stageLabel.${stage}`, { defaultValue: stage }) : '—',
            })}
          </Text>
        </Flex>
      </Box>
    </Paper>
  );
};
